
import React, { useState, useEffect } from 'react';
import { Database, Server, HardDrive, RefreshCw, CheckCircle2, AlertCircle, Table, Trash2, Eye } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import MigrasiKeFirebaseView from '../src/views/MigrasiKeFirebaseView';
import MigrasiKeSupabaseView from '../src/views/MigrasiKeSupabaseView';

const DatabaseManager: React.FC = () => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [localData, setLocalData] = useState<{ key: string, value: string }[]>([]);
  const [previewTable, setPreviewTable] = useState<string>('students');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const fetchPreviewData = async (table: string) => {
    setPreviewTable(table);
    setLoadingPreview(true);
    try {
      const response = await fetch(`/api/${table}`);
      const data = await response.json();
      setPreviewData(Array.isArray(data) ? data.slice(0, 10) : []);
    } catch (err) {
      console.error(err);
      setPreviewData([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const clearTable = async (table: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Kosongkan Tabel',
      message: `PERINGATAN: Anda akan menghapus SEMUA data dari tabel ${table}. Tindakan ini tidak dapat dibatalkan. Lanjutkan?`,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        try {
          const response = await fetch(`/api/clear/${table}`, { method: 'DELETE' });
          if (response.ok) {
            fetchStats();
            fetchPreviewData(table);
          }
        } catch (err) {
          console.error(err);
        }
      }
    });
  };

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/db-stats');
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Gagal mengambil statistik database');
      }
      
      setStats(data);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  };

  const loadLocalData = () => {
    const data = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        data.push({ key, value: localStorage.getItem(key) || '' });
      }
    }
    setLocalData(data);
  };

  const deleteLocalItem = (key: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Cache Lokal',
      message: `Apakah Anda yakin ingin menghapus item "${key}" dari penyimpanan lokal?`,
      onConfirm: () => {
        localStorage.removeItem(key);
        loadLocalData();
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const clearLocalCache = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Hapus Semua Cache',
      message: 'Apakah Anda yakin ingin menghapus semua cache lokal (localStorage)? Ini akan mengeluarkan Anda dari sistem.',
      onConfirm: () => {
        localStorage.clear();
        window.location.reload();
      }
    });
  };

  const generateDummyData = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'Generate Data Dummy',
      message: 'Apakah Anda ingin menambahkan data siswa, jadwal, dan TP secara otomatis untuk keperluan testing?',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        await executeGenerateDummy();
      }
    });
  };

  const executeGenerateDummy = async () => {
    setIsGenerating(true);
    try {
      const initialClasses = ['7A', '7B', '7C', '8A', '8B', '8C', '9A', '9B', '9C', '9D', '9E', '9F', '9G', '9H', '9I', '9J', '9K'];
      const dummyStudents: any[] = [];
      const firstNames = ['Aditya', 'Bima', 'Citra', 'Dian', 'Eka', 'Fajar', 'Ghani', 'Hesty', 'Indra', 'Jati', 'Kurnia', 'Laras', 'Maulana', 'Nadia', 'Okta', 'Pandu', 'Qonita', 'Rizky', 'Sari', 'Taufik', 'Agus', 'Bambang', 'Caca', 'Doni', 'Endang', 'Fitri', 'Guntur', 'Heri', 'Ika', 'Joni'];
      const lastNames = ['Saputra', 'Wicaksono', 'Lestari', 'Putri', 'Kurniawan', 'Hidayat', 'Pratama', 'Sari', 'Utami', 'Setiawan', 'Nugroho', 'Wijaya', 'Ramadhani', 'Fitriani', 'Sanjaya'];
      
      initialClasses.forEach(cls => {
        for (let i = 0; i < 10; i++) {
          dummyStudents.push({
            id: `s-${cls}-${i}-${Date.now()}`,
            nis: `2223${cls.replace(/\D/g, '')}${100 + i}`,
            name: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${lastNames[Math.floor(Math.random() * lastNames.length)]}`,
            class: cls,
            gender: Math.random() > 0.5 ? 'Laki-laki' : 'Perempuan',
            status: 'Aktif'
          });
        }
      });

      const dummyObjectives = [
        { id: 'tp-1', subject: 'Informatika', classLevel: '7', code: 'TP.7.1', description: 'Memahami konsep dasar informatika', semester: 1 },
        { id: 'tp-2', subject: 'Informatika', classLevel: '8', code: 'TP.8.1', description: 'Menerapkan algoritma sederhana', semester: 1 },
        { id: 'tp-3', subject: 'Informatika', classLevel: '9', code: 'TP.9.1', description: 'Membuat program sederhana', semester: 1 }
      ];

      const dummySchedules = [
        { id: 'sch1', day: 'Senin', period: 1, time: '07:30', subject: 'Informatika', class: '9A' },
        { id: 'sch2', day: 'Senin', period: 2, time: '08:10', subject: 'Informatika', class: '9A' },
        { id: 'sch3', day: 'Senin', period: 3, time: '08:50', subject: 'Informatika', class: '9B' },
        { id: 'sch4', day: 'Senin', period: 4, time: '09:30', subject: 'Informatika', class: '9B' },
        { id: 'sch5', day: 'Selasa', period: 1, time: '07:30', subject: 'Informatika', class: '9C' },
        { id: 'sch6', day: 'Selasa', period: 2, time: '08:10', subject: 'Informatika', class: '9C' }
      ];

      const postData = async (endpoint: string, data: any) => {
        await fetch(`/api/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
      };

      await postData('objectives', dummyObjectives);
      await postData('students', dummyStudents);
      await postData('schedules', dummySchedules);

      alert('Data dummy berhasil di-generate! Halaman akan dimuat ulang.');
      window.location.reload();
    } catch (err) {
      alert('Error generating data: ' + String(err));
    } finally {
      setIsGenerating(false);
    }
  };

  const resetDatabase = async () => {
    setConfirmModal({
      isOpen: true,
      title: 'RESET DATABASE CLOUD',
      message: 'PERINGATAN: Ini akan MENGHAPUS SEMUA DATA di database cloud (Supabase). Tindakan ini tidak bisa dibatalkan! Apakah Anda yakin?',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
        setIsDeleting(true);
        try {
          const response = await fetch('/api/db-reset', { method: 'POST' });
          if (!response.ok) throw new Error('Gagal mereset database');
          fetchStats();
        } catch (err) {
          console.error(err);
        } finally {
          setIsDeleting(false);
        }
      }
    });
  };

  useEffect(() => {
    fetchStats();
    loadLocalData();
    fetchPreviewData('students');
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-black text-slate-800 tracking-tight">Status Database</h2>
          <p className="text-slate-500 text-sm">Monitor koneksi dan statistik data sistem</p>
        </div>
        <button 
          onClick={fetchStats}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-all disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Segarkan
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Connection Status Card */}
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-2xl">
              <Server size={24} />
            </div>
            <div>
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Koneksi Server</p>
              <h3 className="text-lg font-bold text-slate-800">Supabase Cloud</h3>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl">
              <span className="text-sm font-medium text-slate-600">Status</span>
              {loading ? (
                <span className="text-sm text-slate-400 italic">Mengecek...</span>
              ) : stats?.status === 'Connected' ? (
                <div className="flex items-center gap-2 text-emerald-600 font-bold text-sm">
                  <CheckCircle2 size={16} />
                  Terhubung
                </div>
              ) : (
                <div className="flex items-center gap-2 text-rose-600 font-bold text-sm">
                  <AlertCircle size={16} />
                  Terputus
                </div>
              )}
            </div>

            <div className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl">
              <span className="text-sm font-medium text-slate-600">Provider</span>
              <span className="text-sm font-bold text-slate-800">{stats?.provider || 'PostgreSQL'}</span>
            </div>

            <div className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl">
              <span className="text-sm font-medium text-slate-600">Lokasi</span>
              <span className="text-sm font-bold text-slate-800">AWS ap-southeast-1</span>
            </div>

            <button 
              onClick={resetDatabase}
              disabled={isDeleting}
              className="w-full flex items-center justify-center gap-2 p-4 bg-rose-50 text-rose-600 rounded-2xl font-bold hover:bg-rose-100 transition-all border border-rose-100"
            >
              <Trash2 size={18} />
              {isDeleting ? 'Menghapus...' : 'Reset Database Cloud'}
            </button>

            <button 
              onClick={generateDummyData}
              disabled={isGenerating}
              className="w-full flex items-center justify-center gap-2 p-4 bg-emerald-50 text-emerald-600 rounded-2xl font-bold hover:bg-emerald-100 transition-all border border-emerald-100 mt-2"
            >
              <Database size={18} />
              {isGenerating ? 'Membuat Data...' : 'Generate Data Dummy'}
            </button>
          </div>
        </div>

        {/* Local Sync Status Card */}
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
          <div className="flex items-center gap-4 mb-6">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <HardDrive size={24} />
            </div>
            <div>
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Penyimpanan Lokal</p>
              <h3 className="text-lg font-bold text-slate-800">Browser Cache</h3>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl">
              <span className="text-sm font-medium text-slate-600">Mode</span>
              <span className="text-sm font-bold text-indigo-600">Online Sync</span>
            </div>

            <div className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl">
              <span className="text-sm font-medium text-slate-600">Auto-Save</span>
              <span className="text-sm font-bold text-emerald-600">Aktif</span>
            </div>

            <button 
              onClick={clearLocalCache}
              className="w-full flex items-center justify-center gap-2 p-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all border border-slate-200"
            >
              <Trash2 size={18} />
              Hapus Cache Lokal
            </button>

            <div className="p-4 bg-indigo-50/50 rounded-2xl border border-indigo-100">
              <p className="text-[10px] text-indigo-600 font-black uppercase tracking-widest mb-1">Catatan</p>
              <p className="text-xs text-indigo-800 leading-relaxed">
                Sistem menggunakan Prisma ORM untuk sinkronisasi otomatis antara antarmuka dan database Supabase.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table Stats */}
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex items-center gap-3 mb-8">
          <Table className="text-indigo-600" size={24} />
          <h3 className="text-xl font-bold text-slate-800">Statistik Tabel</h3>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-100 rounded-2xl text-rose-600 text-sm font-medium flex items-center gap-3">
            <AlertCircle size={18} />
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {[
            { label: 'Guru', key: 'teachers', color: 'bg-blue-50 text-blue-600' },
            { label: 'Siswa', key: 'students', color: 'bg-emerald-50 text-emerald-600' },
            { label: 'Agenda', key: 'agendas', color: 'bg-amber-50 text-amber-600' },
            { label: 'Absensi', key: 'attendance', color: 'bg-rose-50 text-rose-600' },
            { label: 'Tujuan Pembelajaran', key: 'objectives', color: 'bg-purple-50 text-purple-600' },
            { label: 'Jadwal', key: 'schedules', color: 'bg-indigo-50 text-indigo-600' },
            { label: 'Nilai Sumatif', key: 'assessment', color: 'bg-cyan-50 text-cyan-600' },
            { label: 'Nilai SAS', key: 'sas', color: 'bg-amber-50 text-amber-600' },
            { label: 'Nilai Formatif', key: 'formative', color: 'bg-orange-50 text-orange-600' },
            { label: 'Tugas', key: 'tugas', color: 'bg-pink-50 text-pink-600' },
            { label: 'Jawaban Tugas', key: 'submissions', color: 'bg-teal-50 text-teal-600' },
          ].map((item) => (
            <div key={item.key} className="p-5 bg-slate-50 rounded-2xl border border-slate-100 hover:border-indigo-200 transition-all group">
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 group-hover:text-indigo-500 transition-colors">{item.label}</p>
              <div className="flex items-end justify-between">
                <span className="text-2xl font-black text-slate-800">
                  {loading ? '...' : stats?.counts?.[item.key] || 0}
                </span>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-lg ${item.color}`}>
                  Data
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dummy Data Manager */}
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex items-center gap-3 mb-6">
          <Eye className="text-indigo-600" size={24} />
          <h3 className="text-xl font-bold text-slate-800">Manajemen Data Dummy</h3>
        </div>
        
        <p className="text-sm text-slate-500 mb-6">
          Lihat preview data (maksimal 10 baris pertama) dan hapus data per tabel jika diperlukan.
        </p>

        <div className="flex flex-wrap gap-2 mb-6">
          {[
            { id: 'students', label: 'Data Siswa' },
            { id: 'schedules', label: 'Jadwal Pelajaran' },
            { id: 'objectives', label: 'Tujuan Pembelajaran' },
            { id: 'tugas', label: 'Data Tugas' },
            { id: 'tugas-submissions', label: 'Jawaban Tugas' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => fetchPreviewData(tab.id)}
              className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${
                previewTable === tab.id 
                  ? 'bg-indigo-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex justify-between items-center mb-4">
          <h4 className="font-bold text-slate-800">Preview: {previewTable}</h4>
          <button 
            onClick={() => clearTable(previewTable)}
            className="flex items-center gap-2 px-3 py-1.5 bg-rose-50 text-rose-600 rounded-lg text-xs font-bold hover:bg-rose-100 transition-all"
          >
            <Trash2 size={14} />
            Hapus Semua {previewTable}
          </button>
        </div>

        <div className="overflow-x-auto border border-slate-100 rounded-2xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-bottom border-slate-100">
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest">ID</th>
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {loadingPreview ? (
                <tr>
                  <td colSpan={2} className="p-8 text-center text-slate-400 italic text-sm">
                    Memuat data...
                  </td>
                </tr>
              ) : previewData.length > 0 ? (
                previewData.map((item, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4">
                      <code className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">
                        {item.id?.substring(0, 15) || 'N/A'}...
                      </code>
                    </td>
                    <td className="p-4">
                      <div className="text-xs text-slate-600 font-mono">
                        {previewTable === 'students' && `${item.nis} - ${item.name} (${item.class})`}
                        {previewTable === 'schedules' && `${item.day} - ${item.subject} (${item.class})`}
                        {previewTable === 'objectives' && `${item.code} - ${item.description}`}
                        {previewTable === 'tugas' && `${item.title} (${item.class})`}
                        {previewTable === 'tugas-submissions' && `Tugas: ${item.tugasId} - Skor: ${item.score}`}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} className="p-8 text-center text-slate-400 italic text-sm">
                    Tidak ada data di tabel ini.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Local Storage Detail */}
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex items-center gap-3 mb-6">
          <HardDrive className="text-indigo-600" size={24} />
          <h3 className="text-xl font-bold text-slate-800">Detail Penyimpanan Lokal</h3>
        </div>

        <div className="overflow-hidden border border-slate-100 rounded-2xl">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-bottom border-slate-100">
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest">Key</th>
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest">Value (Preview)</th>
                <th className="p-4 text-xs font-black text-slate-400 uppercase tracking-widest text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {localData.length > 0 ? (
                localData.map((item) => (
                  <tr key={item.key} className="hover:bg-slate-50/50 transition-colors">
                    <td className="p-4">
                      <code className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">
                        {item.key}
                      </code>
                    </td>
                    <td className="p-4">
                      <div className="max-w-md truncate text-xs text-slate-500 font-mono">
                        {item.value}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <button 
                        onClick={() => deleteLocalItem(item.key)}
                        className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                        title="Hapus Item"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="p-8 text-center text-slate-400 italic text-sm">
                    Tidak ada data di penyimpanan lokal.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <MigrasiKeFirebaseView />
      <MigrasiKeSupabaseView />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />
    </div>
  );
};

export default DatabaseManager;
